import { useState, useEffect } from 'react';
import type { Asset } from '@/types/asset';
import type { DisposalDetails, DepreciationHandling } from '@/types/disposal';
import type { DisposalReason } from '@/types/settings';
import { dataService } from '@/services/data-service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { AlertCircle, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// Helper function to get disposal reason display text - extracted to fix nested ternary (S3358)
function getDisposalReasonDisplayText(
  disposalReasonId: string | undefined,
  disposalReasons: DisposalReason[]
): string {
  if (!disposalReasonId) {
    return '';
  }
  const reason = disposalReasons.find((r) => r.id === disposalReasonId);
  if (!reason) {
    return '';
  }
  return `${reason.code} - ${reason.description}`;
}

interface DisposalDetailsStepProps {
  readonly asset: Asset;
  readonly details: DisposalDetails;
  readonly onDetailsChange: (details: DisposalDetails) => void;
  readonly onNext: () => void;
  readonly onCancel: () => void;
}

export function DisposalDetailsStep({
  details,
  onDetailsChange,
  onNext,
  onCancel,
}: DisposalDetailsStepProps) {
  const [disposalReasons, setDisposalReasons] = useState<DisposalReason[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasCurrentPeriodDepreciation, setHasCurrentPeriodDepreciation] = useState(false);
  const [openCombobox, setOpenCombobox] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [, setLoading] = useState(true);

  useEffect(() => {
    loadDisposalReasons();
    // In a real implementation, check if there's current period depreciation
    // For now, we'll simulate this
    setHasCurrentPeriodDepreciation(false);
  }, []);

  const loadDisposalReasons = async () => {
    try {
      const reasons = await dataService.getDisposalReasons();
      setDisposalReasons(reasons);
    } catch (error) {
      console.error('Failed to load disposal reasons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof DisposalDetails, value: any) => {
    onDetailsChange({ ...details, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleDateChange = (date: string) => {
    // In a real implementation, calculate year and period from the date
    // using the organisation's accounting calendar
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const period = dateObj.getMonth() + 1;

    onDetailsChange({
      ...details,
      date,
      year,
      period,
    });

    if (errors.date) {
      setErrors({ ...errors, date: '' });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!details.reference.trim()) {
      newErrors.reference = 'Reference is required';
    } else if (details.reference.length > 10) {
      newErrors.reference = 'Reference must be 10 characters or less';
    }

    if (!details.date) {
      newErrors.date = 'Date is required';
    }

    if (!details.disposalReasonId) {
      newErrors.disposalReasonId = 'Disposal reason is required';
    }

    if (hasCurrentPeriodDepreciation && !details.depreciationHandling) {
      newErrors.depreciationHandling = 'Please select how to handle current period depreciation';
    }

    if (details.notes && details.notes.length > 500) {
      newErrors.notes = 'Notes must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="space-y-4">
      {/* Transaction Details Card */}
      <Card>
        <CardContent className="space-y-3 pt-6">
          {/* Transaction Fields */}
          <div className="space-y-3">
            {/* Reference and Disposal Reason */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="reference" className="text-sm">
                  Reference <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="reference"
                  placeholder="Enter reference"
                  value={details.reference}
                  onChange={(e) => handleFieldChange('reference', e.target.value)}
                  maxLength={10}
                  className={errors.reference ? 'border-destructive' : ''}
                />
                {errors.reference && (
                  <p className="text-xs text-destructive">{errors.reference}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="disposalReason" className="text-sm">
                  Disposal Reason <span className="text-destructive">*</span>
                </Label>
            <Popover open={openCombobox} onOpenChange={setOpenCombobox} modal={false}>
              <PopoverTrigger asChild>
                <div className="relative">
                  <Input
                    id="disposalReason"
                    placeholder="Search or select a disposal reason..."
                    value={searchValue || getDisposalReasonDisplayText(details.disposalReasonId, disposalReasons)}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      if (!openCombobox) {
                        setOpenCombobox(true);
                      }
                    }}
                    onFocus={(e) => {
                      e.preventDefault();
                      setOpenCombobox(true);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenCombobox(true);
                    }}
                    className={cn(
                      "pr-8",
                      errors.disposalReasonId && "border-destructive"
                    )}
                  />
                  <ChevronsUpDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                </div>
              </PopoverTrigger>
              <PopoverContent 
                className="w-[250px] p-0" 
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
                onInteractOutside={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.id === 'disposalReason') {
                    e.preventDefault();
                  }
                }}
              >
                <Command>
                  <CommandList className="max-h-[120px]">
                    <CommandEmpty>No disposal reason found.</CommandEmpty>
                    <CommandGroup>
                      {disposalReasons
                        .filter((reason) => {
                          if (!searchValue) return true;
                          const search = searchValue.toLowerCase();
                          return (
                            reason.code.toLowerCase().includes(search) ||
                            reason.description.toLowerCase().includes(search)
                          );
                        })
                        .map((reason) => (
                          <CommandItem
                            key={reason.id}
                            value={`${reason.code} ${reason.description}`}
                            onSelect={() => {
                              handleFieldChange('disposalReasonId', reason.id);
                              setSearchValue('');
                              setOpenCombobox(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                details.disposalReasonId === reason.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {reason.code} - {reason.description}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
                {errors.disposalReasonId && (
                  <p className="text-xs text-destructive">{errors.disposalReasonId}</p>
                )}
              </div>
            </div>

            {/* Date, Year and Period */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="date" className="text-sm">
                  Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={details.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className={errors.date ? 'border-destructive' : ''}
                />
                {errors.date && (
                  <p className="text-xs text-destructive">{errors.date}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="year" className="text-sm">Year</Label>
                <Input
                  id="year"
                  value={details.year}
                  readOnly
                  className="bg-muted"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="period" className="text-sm">Period</Label>
                <Input
                  id="period"
                  value={details.period}
                  readOnly
                  className="bg-muted"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="notes" className="text-sm">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Optional notes (max 500 characters)"
                value={details.notes ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('notes', e.target.value)}
                maxLength={500}
                rows={2}
                className={errors.notes ? 'border-destructive' : ''}
              />
              {errors.notes && (
                <p className="text-xs text-destructive">{errors.notes}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {(details.notes ?? '').length}/500 characters
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conditional Depreciation Notice */}
      {hasCurrentPeriodDepreciation && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-3">
              <p>
                Depreciation has been posted in the current period for this asset. Please select whether this depreciation should be reversed or disposed of.
              </p>
              <RadioGroup
                value={details.depreciationHandling}
                onValueChange={(value: string) => handleFieldChange('depreciationHandling', value as DepreciationHandling)}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reverse" id="reverse" />
                  <Label htmlFor="reverse">Reverse current period depreciation</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="dispose" id="dispose" />
                  <Label htmlFor="dispose">Dispose current period depreciation</Label>
                </div>
              </RadioGroup>
              {errors.depreciationHandling && (
                <p className="text-sm text-destructive">{errors.depreciationHandling}</p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
